<template>
  <button
    type="button"
    class="control-toggle-row"
    role="switch"
    :aria-checked="modelValue"
    :aria-label="label"
    :style="rootStyle"
    @click="emit('update:modelValue', !modelValue)">
    <span class="control-toggle-row-label">
      <ControlIcon v-if="icon" :name="icon" size="1.15em" />
      <span class="control-toggle-row-text"
        ><slot>{{ label }}</slot></span
      >
    </span>
    <span class="control-toggle" :class="{ 'control-toggle--on': modelValue }" aria-hidden="true">
      <i class="control-toggle-knob" />
    </span>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import ControlIcon from '../device-icons/ControlIcon.vue';
import { sheetAccentStyle } from './sheetAccent';

const props = withDefaults(
  defineProps<{
    modelValue: boolean;
    /** The row's text — and its accessible name (the whole row is the switch). */
    label: string;
    /** A ControlIcon name drawn before the label. */
    icon?: string;
    accent?: string;
  }>(),
  { icon: '' },
);

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const rootStyle = computed(() => sheetAccentStyle(props.accent));
</script>

<style scoped>
.control-toggle-row {
  /* The sheet's accent, or the light accent when nothing has set one. */
  --toggle-accent: var(--sheet-accent, var(--device-color-light));
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: calc(0.75rem * var(--ui-space-factor));
  width: 100%;
  padding: calc(0.5625rem * var(--ui-space-factor)) 0;
  border: 0;
  /* The hairline under every row, including the last — the family's divider. */
  border-bottom: 1px solid var(--glass-edge-soft);
  background: none;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.control-toggle-row-label {
  display: inline-flex;
  align-items: center;
  gap: 0.5em;
  min-width: 0;
  font-size: 0.8125rem;
  color: rgb(var(--gray-700));
}

.dark .control-toggle-row-label {
  color: rgb(var(--gray-200));
}

.control-toggle-row-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.control-toggle {
  position: relative;
  width: 2.625rem;
  height: 1.5rem;
  flex-shrink: 0;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.28);
  border: 1px solid var(--glass-edge);
  transition:
    background 0.18s,
    border-color 0.18s;
}

.control-toggle-knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 1.125rem;
  height: 1.125rem;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
  transition: left 0.18s;
}

.control-toggle--on {
  background: color-mix(in srgb, var(--toggle-accent) 30%, rgba(255, 255, 255, 0.06));
  border-color: color-mix(in srgb, var(--toggle-accent) 45%, transparent);
}

.control-toggle--on .control-toggle-knob {
  left: auto;
  right: 2px;
}

.control-toggle-row:focus-visible {
  outline: 2px solid color-mix(in srgb, var(--toggle-accent) 60%, transparent);
  outline-offset: 2px;
  border-radius: 6px;
}
</style>
